import React, { useMemo, useRef, useState } from 'react';
import {
  Button,
  Icon,
  Popover,
  TextInput,
  Typography,
} from 'glints-aries/es/@next';
import { useIntl } from 'react-intl';

import { PhoneUtils } from '../../../../../common/helpers/phone';
import {
  CountryCallingCodeOption,
  useCountryCallingCodeOptions,
} from '../../../../../common/hooks/useCountryCallingCodeOptions';
import * as Styled from './styles.sc';

export const SHOW_CALLING_CODE_SEARCH_BAR_COUNT_THRESHOLD = 20;
interface CountryCallingCodeDropdownProps {
  isWhatsAppInput: boolean;
  callingCode?: string;
  onChange: (option: CountryCallingCodeOption) => void;
}

export const CountryCallingCodeDropdown = ({
  isWhatsAppInput,
  callingCode,
  onChange,
}: CountryCallingCodeDropdownProps) => {
  const { formatMessage } = useIntl();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [filterInput, setFilterInput] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { callingCodeOptions, countryCodeOptionsAreLoading } =
    useCountryCallingCodeOptions({
      filterInput,
      isWhatsAppInput,
    });

  const selectedOption = useMemo(
    () =>
      callingCodeOptions.find(
        option =>
          PhoneUtils.normalizeCallingCode(option.callingCode) ===
          PhoneUtils.normalizeCallingCode(callingCode)
      ),
    [callingCodeOptions, callingCode]
  );

  const { featuredOptions, otherOptions } = useMemo(() => {
    const sortedCallingCodeOptions = [...callingCodeOptions].sort(
      (leftOption, rightOption) =>
        leftOption.countryName.localeCompare(rightOption.countryName)
    );

    return {
      featuredOptions: sortedCallingCodeOptions.filter(
        option => option.isFeatured
      ),
      otherOptions: sortedCallingCodeOptions.filter(
        option => !option.isFeatured
      ),
    };
  }, [callingCodeOptions]);

  const handleOptionSelect = (option: CountryCallingCodeOption) => {
    onChange(option);
    setIsMenuOpen(false);
    setFilterInput('');
  };

  const dropdownButton = (
    <Typography variant="body1">
      <Button
        type="button"
        onClick={() => setIsMenuOpen(previousState => !previousState)}
      >
        {selectedOption?.callingCode ?? callingCode ?? ''}
        <Icon
          name={isMenuOpen ? 'ri-arrow-m-up-line' : 'ri-arrow-m-down-line'}
          width={20}
          height={20}
        />
      </Button>
    </Typography>
  );
  const showFeatured = featuredOptions.length > 0;
  const showOthers = otherOptions.length > 0;
  return (
    <Styled.CallingCodeDropdownContainer ref={dropdownRef}>
      <Popover
        zIndexOverride={1000}
        fluidContent={true}
        preferredAlignment={'left'}
        active={isMenuOpen}
        activator={dropdownButton}
        onClose={() => setIsMenuOpen(false)}
      >
        <Choose>
          <When
            condition={
              callingCodeOptions.length >
              SHOW_CALLING_CODE_SEARCH_BAR_COUNT_THRESHOLD
            }
          >
            <Styled.CallingCodeDropdownSearch>
              <TextInput
                autoFocus={true}
                prefix={<Icon name="ri-search" />}
                ref={searchInputRef}
                value={filterInput}
                onChange={event => setFilterInput(event)}
                placeholder={formatMessage({
                  id: 'phoneNumberInput.callingCodeFilterInputPlaceholder',
                  defaultMessage: 'Type country code or country name',
                })}
              />
            </Styled.CallingCodeDropdownSearch>
          </When>
        </Choose>
        <Styled.CallingCodeDropdownOptions>
          {countryCodeOptionsAreLoading && (
            <Styled.CallingCodeDropdownNoOptions>
              {formatMessage({
                id: 'text-loading',
                defaultMessage: 'Loading...',
              })}
            </Styled.CallingCodeDropdownNoOptions>
          )}
          {!countryCodeOptionsAreLoading && (
            <>
              {showFeatured && (
                <CallingCodeOptionsSection
                  title={
                    showOthers
                      ? formatMessage({
                          id: 'text-popular',
                          defaultMessage: 'POPULAR',
                        })
                      : undefined
                  }
                  options={featuredOptions}
                  onOptionSelect={handleOptionSelect}
                />
              )}
              {otherOptions.length > 0 && (
                <CallingCodeOptionsSection
                  title={
                    showFeatured
                      ? formatMessage({
                          id: 'text-others',
                          defaultMessage: 'Others',
                        })
                      : undefined
                  }
                  options={otherOptions}
                  onOptionSelect={handleOptionSelect}
                />
              )}
              {featuredOptions.length === 0 && otherOptions.length === 0 && (
                <Styled.CallingCodeDropdownNoOptions>
                  {formatMessage(
                    {
                      id: 'text-no-results-found',
                      defaultMessage: 'No results found',
                    },
                    { searchTerm: filterInput }
                  )}
                </Styled.CallingCodeDropdownNoOptions>
              )}
            </>
          )}
        </Styled.CallingCodeDropdownOptions>
      </Popover>
    </Styled.CallingCodeDropdownContainer>
  );
};

function CallingCodeOptionsSection({
  title,
  options,
  onOptionSelect,
}: {
  title?: string;
  options: CountryCallingCodeOption[];
  onOptionSelect: (option: CountryCallingCodeOption) => void;
}) {
  return (
    <>
      <Choose>
        <When condition={Boolean(title)}>
          <Styled.CallingCodeDropdownSectionTitle>
            {title}
          </Styled.CallingCodeDropdownSectionTitle>
        </When>
      </Choose>
      {options.map(option => (
        <CallingCodeOptionRow
          key={`${option.countryCode}-${option.callingCode}`}
          option={option}
          onOptionSelect={onOptionSelect}
        />
      ))}
    </>
  );
}

function CallingCodeOptionRow({
  option,
  onOptionSelect,
}: {
  option: CountryCallingCodeOption;
  onOptionSelect: (option: CountryCallingCodeOption) => void;
}) {
  return (
    <Typography variant="body1">
      <Styled.CallingCodeDropdownOption
        type="button"
        onClick={() => onOptionSelect(option)}
      >
        <Styled.CallingCodeDropdownOptionCode>
          {option.callingCode}
        </Styled.CallingCodeDropdownOptionCode>
        <Styled.CallingCodeDropdownOptionName>
          {option.countryName}
        </Styled.CallingCodeDropdownOptionName>
      </Styled.CallingCodeDropdownOption>
    </Typography>
  );
}
